import { TableCellNode } from '@lexical/table';

/**
 * Patches TableCellNode to support per-cell border color (including 'none'
 * for no-border and any CSS color string).  Follows the same pattern as
 * StyledParagraphNode.jsx — must be imported before the editor is created.
 *
 * borderColor values:
 *   null              → no inline style; SCSS default border applies
 *   'none'            → border: none (completely removed)
 *   'transparent'     → border-color: transparent (space preserved, invisible)
 *   '#RRGGBB' / name  → border-width: 1px; border-style: solid; border-color: <value>
 */

// Set the prototype default so all instances have the property.
TableCellNode.prototype.__borderColor = null;

// ── Accessors ─────────────────────────────────────────────────────────────────

TableCellNode.prototype.getBorderColor = function () {
  return this.__borderColor ?? null;
};

TableCellNode.prototype.setBorderColor = function (color) {
  const w = this.getWritable();
  w.__borderColor = color ?? null;
  return w;
};

// ── DOM helper ────────────────────────────────────────────────────────────────

function applyBorderToDOM(borderColor, el) {
  if (!borderColor) {
    // Restore — remove only the properties we may have set.
    el.style.removeProperty('border');
    el.style.removeProperty('border-width');
    el.style.removeProperty('border-style');
    el.style.removeProperty('border-color');
  } else if (borderColor === 'none') {
    el.style.border = 'none';
  } else if (borderColor === 'transparent') {
    el.style.removeProperty('border');
    el.style.borderColor = 'transparent';
  } else {
    el.style.removeProperty('border');
    el.style.borderWidth = '1px';
    el.style.borderStyle = 'solid';
    el.style.borderColor = borderColor;
  }
}

// ── createDOM ─────────────────────────────────────────────────────────────────

const _origCreateDOM = TableCellNode.prototype.createDOM;
TableCellNode.prototype.createDOM = function (config, editor) {
  const dom = _origCreateDOM.call(this, config, editor);
  if (this.__borderColor) applyBorderToDOM(this.__borderColor, dom);
  return dom;
};

// ── updateDOM ─────────────────────────────────────────────────────────────────

const _origUpdateDOM = TableCellNode.prototype.updateDOM;
TableCellNode.prototype.updateDOM = function (prevNode, dom, config) {
  const result = _origUpdateDOM.call(this, prevNode, dom, config);
  if (prevNode.__borderColor !== this.__borderColor) {
    applyBorderToDOM(this.__borderColor, dom);
  }
  return result;
};

// ── exportJSON ────────────────────────────────────────────────────────────────

const _origExportJSON = TableCellNode.prototype.exportJSON;
TableCellNode.prototype.exportJSON = function () {
  const json = _origExportJSON.call(this);
  if (this.__borderColor) json.borderColor = this.__borderColor;
  return json;
};

// ── importJSON (static) ───────────────────────────────────────────────────────

const _origImportJSON = TableCellNode.importJSON;
TableCellNode.importJSON = function (serializedNode) {
  const node = _origImportJSON.call(this, serializedNode);
  if (serializedNode.borderColor) node.__borderColor = serializedNode.borderColor;
  return node;
};

// ── clone (static) ────────────────────────────────────────────────────────────

const _origClone = TableCellNode.clone;
TableCellNode.clone = function (node) {
  const cloned = _origClone.call(this, node);
  cloned.__borderColor = node.__borderColor ?? null;
  return cloned;
};

// ── exportDOM — applies border with !important for PDF / print HTML ───────────

const _origExportDOM = TableCellNode.prototype.exportDOM;
TableCellNode.prototype.exportDOM = function (editor) {
  const result = _origExportDOM.call(this, editor);
  if (result.element && this.__borderColor) {
    const el = result.element;
    if (this.__borderColor === 'none') {
      el.style.setProperty('border', 'none', 'important');
    } else if (this.__borderColor === 'transparent') {
      el.style.setProperty('border-color', 'transparent', 'important');
    } else {
      el.style.setProperty('border-width', '1px',  'important');
      el.style.setProperty('border-style', 'solid', 'important');
      el.style.setProperty('border-color', this.__borderColor, 'important');
    }
  }
  return result;
};
